import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Calendar, DollarSign, ExternalLink, AlertTriangle, FileText, Package, Download, History, TrendingUp } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useEmpresaDocumentos } from '@/hooks/useEmpresa';
import { useAgenteIaJob } from '@/hooks/useAgenteIaJob';
import { useOraculoResultado } from '@/hooks/useOraculoEdital';
import { useAdicionarAoPipeline } from '@/hooks/useGestaoLicitacoes';
import { useLicitacaoItens, useLicitacaoArquivos, useLicitacaoHistorico } from '@/hooks/useLicitacaoPncpDetalhes';
import { useBenchmarkMercado } from '@/hooks/useBenchmarkMercado';
import { riscoBadgeVariant } from '@/lib/riskBadge';
import type { LicitacaoFeed } from '@/hooks/useLicitacoes';
import type { EmpresaListItem } from '@/types/empresa';
import type { OraculoAnalise, Recomendacao } from '@/types/agentesIa';
import { useViewModeStore } from '@/stores/view-mode';

interface LicitacaoFeedDetalhesModalProps {
  licitacao: LicitacaoFeed | null;
  empresas: EmpresaListItem[];
  onClose: () => void;
  /** Termo buscado — destacado no objeto da licitação (M5). */
  termoDestaque?: string;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Destaca ocorrências do termo (case-insensitive) no texto com <mark>. */
function destacarTermo(texto: string, termo?: string) {
  const alvo = (termo || '').trim();
  if (!alvo) return texto;
  const partes = texto.split(new RegExp(`(${escapeRegExp(alvo)})`, 'ig'));
  return partes.map((parte, i) =>
    parte.toLowerCase() === alvo.toLowerCase()
      ? <mark key={i} className="bg-ds-warning/30 text-ds-text-primary rounded px-0.5">{parte}</mark>
      : parte
  );
}

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-success/50 focus:border-ds-success/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

const RECOMENDACAO_MAP: Record<Recomendacao, { label: string; variant: BadgeVariant }> = {
  participar: { label: 'Participar', variant: 'success' },
  declinar: { label: 'Declinar', variant: 'danger' },
  analise_manual: { label: 'Análise manual', variant: 'warning' },
};

const EMPRESA_PRE_DISPUTA_ACTIONS = [
  { tipo: 'pedido_esclarecimento', label: 'Pedir esclarecimento', icon: FileText },
] as const;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('pt-BR');

const formatMoney = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('pt-BR');
};

const formatCnpj = (cnpj: string) => {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const ESFERA_LABEL: Record<string, string> = {
  federal: 'Federal',
  estadual: 'Estadual',
  municipal: 'Municipal',
  distrital: 'Distrital',
};

/**
 * Amparo legal derivado da modalidade (Lei 14.133/2021), determinístico e client-side.
 * Tolerante a variações de rótulo ("Pregão - Eletrônico", "Pregão Eletrônico", etc.).
 * Retorna null para modalidades sem mapeamento — a seção fica oculta.
 */
function amparoLegal(modalidade: string | null): { lei: string; descricao: string } | null {
  if (!modalidade) return null;
  const m = modalidade
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  if (m.includes('pregao')) {
    return { lei: 'Lei 14.133/2021, Art. 28, I', descricao: 'Pregão: modalidade para aquisição de bens e serviços comuns.' };
  }
  if (m.includes('concorrencia')) {
    return { lei: 'Lei 14.133/2021, Art. 28, II', descricao: 'Concorrência: bens e serviços especiais e obras e serviços de engenharia.' };
  }
  if (m.includes('concurso')) {
    return { lei: 'Lei 14.133/2021, Art. 28, III', descricao: 'Concurso: escolha de trabalho técnico, científico ou artístico.' };
  }
  if (m.includes('leilao')) {
    return { lei: 'Lei 14.133/2021, Art. 28, IV', descricao: 'Leilão: alienação de bens imóveis ou móveis inservíveis ou legalmente apreendidos.' };
  }
  if (m.includes('dialogo')) {
    return { lei: 'Lei 14.133/2021, Art. 28, V', descricao: 'Diálogo competitivo: contratações que envolvam inovação tecnológica ou soluções não disponíveis no mercado.' };
  }
  if (m.includes('dispensa')) {
    return { lei: 'Lei 14.133/2021, Art. 75', descricao: 'Contratação direta por dispensa de licitação.' };
  }
  if (m.includes('inexig')) {
    return { lei: 'Lei 14.133/2021, Art. 74', descricao: 'Contratação direta por inexigibilidade (inviabilidade de competição).' };
  }
  return null;
}

function extractErrorMessage(error: unknown): string {
  const err = error as { error?: { message?: string }; message?: string } | null;
  return err?.error?.message || err?.message || 'Não foi possível concluir a operação.';
}

function AderenciaBar({ score }: { score: number }) {
  let color = 'bg-ds-danger';
  if (score >= 75) color = 'bg-ds-success';
  else if (score >= 50) color = 'bg-ds-warning';
  else if (score >= 25) color = 'bg-ds-info';

  return (
    <div className="flex items-center gap-3">
      <div className="w-32 h-2 rounded-full bg-ds-surface2 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-sm font-medium text-ds-text-primary">{score}/100</span>
    </div>
  );
}

interface FonteExterna {
  url: string;
  rotulo: string;
}

interface PncpEstadoProps {
  isLoading: boolean;
  isError: boolean;
  indisponivel?: boolean;
  origemRecusou?: boolean;
  fonteExterna?: FonteExterna | null;
  vazio: boolean;
  onRetry: () => void;
}

/** Estados de degradação graciosa compartilhados pelas seções PNCP. Retorna null quando há dados. */
function PncpEstado({ isLoading, isError, indisponivel, origemRecusou, fonteExterna, vazio, onRetry }: PncpEstadoProps) {
  if (isLoading) return <p className="text-sm text-ds-text-secondary">Carregando…</p>;
  if (indisponivel) {
    // Descoberta do agregador: os itens/arquivos não são publicados aqui — ficam no portal de origem.
    if (fonteExterna?.url) {
      return (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-ds-text-secondary">
            Itens e documentos não foram publicados no PNCP para este edital. Consulte no portal de origem.
          </p>
          <a href={fonteExterna.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <Button type="button" variant="secondary" size="sm">Ver no portal de origem</Button>
          </a>
        </div>
      );
    }
    return <p className="text-sm text-ds-text-secondary">Esta licitação não tem identificador PNCP para consulta detalhada.</p>;
  }
  if (isError || origemRecusou) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-ds-text-secondary">A consulta foi recusada pela origem (PNCP).</p>
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>Tentar novamente</Button>
      </div>
    );
  }
  if (vazio) return <p className="text-sm text-ds-text-secondary">Nenhum registro encontrado.</p>;
  return null;
}

function LicitacaoFeedDetalhesModal({ licitacao, empresas, onClose, termoDestaque }: LicitacaoFeedDetalhesModalProps) {
  const { mode, previewEmpresaId } = useViewModeStore();
  const isEmpresaView = mode === 'empresa' && previewEmpresaId !== null;
  const empresaSelecionada = empresas.find((empresa) => empresa.id === previewEmpresaId);
  const [empresaId, setEmpresaId] = useState('');
  const [documentoId, setDocumentoId] = useState('');

  const empresaIdNum = parseInt(empresaId, 10) || undefined;

  const { data: documentos, isLoading: documentosLoading } = useEmpresaDocumentos(empresaId || undefined);
  const { enqueue, isEnqueuing, jobStatus, reset } = useAgenteIaJob<
    { licitacaoId: number; documentoId: number },
    OraculoAnalise
  >('oraculo-edital', empresaIdNum);
  const { data: resultado, isLoading: resultadoLoading } = useOraculoResultado(
    empresaIdNum,
    licitacao?.id,
    jobStatus?.status
  );
  const adicionar = useAdicionarAoPipeline(empresaIdNum);

  // Seções PNCP sob demanda (MOD.2): só buscam ao expandir.
  const [openItens, setOpenItens] = useState(false);
  const [openArquivos, setOpenArquivos] = useState(false);
  const [openHistorico, setOpenHistorico] = useState(false);
  const itensQuery = useLicitacaoItens(licitacao?.id, openItens);
  const arquivosQuery = useLicitacaoArquivos(licitacao?.id, openArquivos);
  const historicoQuery = useLicitacaoHistorico(licitacao?.id, openHistorico);

  // Inteligência de Mercado (IM.2): detecta o CATMAT do primeiro item com código.
  const [openMercado, setOpenMercado] = useState(false);
  const [codigoManual, setCodigoManual] = useState('');
  const itemComCodigo = (itensQuery.data?.itens || []).find((i) => i.catalogo_codigo);
  const codigoDetectado = itemComCodigo?.catalogo_codigo || null;
  const tipoDetectado: 'material' | 'servico' =
    itemComCodigo?.material_servico && /servi/i.test(itemComCodigo.material_servico) ? 'servico' : 'material';
  const codigoAtivo = codigoManual.trim() || codigoDetectado;
  const benchmarkQuery = useBenchmarkMercado(codigoAtivo, codigoManual.trim() ? 'material' : tipoDetectado, openMercado);

  const isRunning = Boolean(jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed');

  useEffect(() => {
    setEmpresaId(isEmpresaView ? String(previewEmpresaId) : '');
    setDocumentoId('');
  }, [licitacao?.id, isEmpresaView, previewEmpresaId]);

  useEffect(() => {
    setDocumentoId('');
    reset();
    adicionar.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  if (!licitacao) return null;

  const isVencida = licitacao.status_calculado === 'vencida';
  const isIncompleta = licitacao.campos_ausentes?.length > 0;
  const documentoIdNum = parseInt(documentoId, 10) || undefined;

  const handleConsultar = async () => {
    if (!empresaIdNum || !documentoIdNum) return;
    reset();
    await enqueue({ licitacaoId: licitacao.id, documentoId: documentoIdNum });
  };

  return (
    <Modal isOpen onClose={onClose} title="Detalhes da licitação" contentClassName="max-w-4xl">
      <div className="space-y-5">
        <div>
          <div className="flex items-start justify-between gap-3">
            <h4 className="text-base font-semibold text-ds-text-primary">{destacarTermo(licitacao.objeto, termoDestaque)}</h4>
            {isVencida ? <Badge variant="default">Vencida</Badge> : <Badge variant="success">Ativa</Badge>}
          </div>
          <div className="flex items-center gap-2 text-sm text-ds-text-secondary mt-1">
            <Building2 className="h-4 w-4" />
            {licitacao.orgao || 'Órgão não informado'}
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Badge size="sm">{licitacao.portal}</Badge>
            {licitacao.modalidade && <Badge size="sm">{licitacao.modalidade}</Badge>}
            {licitacao.uf && <Badge size="sm">{licitacao.uf}{licitacao.municipio ? ` — ${licitacao.municipio}` : ''}</Badge>}
            {licitacao.capag_nota && (
              <span title="Capacidade de Pagamento do município (Tesouro Nacional). Quanto melhor a nota, menor o risco de inadimplência do ente.">
                <Badge variant="info" size="sm">CAPAG: {licitacao.capag_nota}</Badge>
              </span>
            )}
            {licitacao.situacao_pncp && <Badge size="sm">{licitacao.situacao_pncp}</Badge>}
            {isIncompleta && (
              <span
                className="flex items-center gap-1 text-xs text-ds-warning cursor-default"
                title={`Campos ausentes: ${licitacao.campos_ausentes.join(', ')}`}
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Incompleta
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 rounded-lg border border-ds-border bg-ds-surface2 p-3">
            <div>
              <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide flex items-center gap-1">
                <DollarSign className="h-3 w-3" /> Valor estimado
              </p>
              <p className="text-sm font-semibold text-ds-success">
                {licitacao.valor_estimado != null ? formatCurrency(Number(licitacao.valor_estimado)) : 'Não informado'}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Prazo de abertura
              </p>
              <p className="text-sm font-medium text-ds-text-primary">
                {licitacao.prazo_abertura ? formatDate(licitacao.prazo_abertura) : 'Indefinido'}
              </p>
            </div>
            {licitacao.numero_edital && (
              <div>
                <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide">Número do edital</p>
                <p className="text-sm text-ds-text-primary">{licitacao.numero_edital}</p>
              </div>
            )}
            {licitacao.numero_controle_pncp && (
              <div>
                <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide">Controle PNCP</p>
                <p className="text-sm text-ds-text-primary break-all">{licitacao.numero_controle_pncp}</p>
              </div>
            )}
            {licitacao.esfera && (
              <div>
                <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide">Esfera</p>
                <p className="text-sm text-ds-text-primary">{ESFERA_LABEL[licitacao.esfera.toLowerCase()] || licitacao.esfera}</p>
              </div>
            )}
            {licitacao.orgao_cnpj && (
              <div>
                <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide">CNPJ do órgão</p>
                <p className="text-sm text-ds-text-primary">{formatCnpj(licitacao.orgao_cnpj)}</p>
              </div>
            )}
          </div>

          {(() => {
            const amparo = amparoLegal(licitacao.modalidade);
            if (!amparo) return null;
            return (
              <div className="mt-3 rounded-lg border border-ds-border bg-ds-surface2 p-3">
                <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide">Amparo legal</p>
                <p className="text-sm font-medium text-ds-text-primary">{amparo.lei}</p>
                <p className="text-xs text-ds-text-secondary mt-0.5">{amparo.descricao}</p>
              </div>
            );
          })()}

          {licitacao.ia_processado && licitacao.resumo_ia && (
            <div className="mt-3 rounded-lg border border-ds-border bg-ds-surface2 p-3 space-y-2">
              <p className="text-xs font-semibold text-ds-text-secondary uppercase tracking-wide">Resumo (IA)</p>
              <p className="text-sm text-ds-text-primary">{licitacao.resumo_ia}</p>
              <div className="flex flex-wrap items-center gap-1.5">
                {(licitacao.categorias_ia || []).slice(0, 5).map((cat) => (
                  <Badge key={cat} size="sm">{cat}</Badge>
                ))}
                {licitacao.relevancia_score !== null && (
                  <Badge variant="success" size="sm">Relevância IA {licitacao.relevancia_score}%</Badge>
                )}
              </div>
            </div>
          )}

          {licitacao.aderencias && licitacao.aderencias.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-ds-text-secondary uppercase tracking-wide mb-1.5">Já avaliada para</p>
              <div className="flex flex-wrap gap-1.5">
                {licitacao.aderencias.map((aderencia) => (
                  <Badge
                    key={aderencia.empresa_id}
                    size="sm"
                    variant={
                      aderencia.recomendacao === 'participar' ? 'success'
                        : aderencia.recomendacao === 'declinar' ? 'danger'
                          : 'warning'
                    }
                  >
                    {aderencia.empresa_nome}
                    {aderencia.aderencia_score !== null ? ` — ${aderencia.aderencia_score}%` : ''}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 rounded-lg border border-ds-border bg-ds-surface2 p-3">
            <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide mb-1">Local da disputa</p>
            {licitacao.link ? (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ds-text-primary">{licitacao.portal}</p>
                  <p className="text-xs text-ds-text-secondary truncate">{licitacao.link}</p>
                </div>
                <a href={licitacao.link} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <Button type="button" variant="secondary" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Acessar
                  </Button>
                </a>
              </div>
            ) : (
              <p className="text-sm text-ds-text-secondary">Link do portal não informado.</p>
            )}
          </div>

          <div className="mt-3 space-y-2">
            <CollapsibleSection
              title="Itens da contratação"
              icon={<Package className="h-4 w-4 text-ds-text-secondary" />}
              badge={itensQuery.data && !itensQuery.data.indisponivel ? `(${itensQuery.data.itens.length})` : undefined}
              onOpenChange={setOpenItens}
            >
              {(() => {
                const d = itensQuery.data;
                const estado = (
                  <PncpEstado
                    isLoading={itensQuery.isLoading}
                    isError={itensQuery.isError}
                    indisponivel={d?.indisponivel}
                    origemRecusou={d?.origem_recusou}
                    fonteExterna={d?.fonte_externa}
                    vazio={Boolean(d && d.itens.length === 0)}
                    onRetry={() => itensQuery.refetch()}
                  />
                );
                if (!d || d.indisponivel || d.origem_recusou || d.itens.length === 0) return estado;
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-ds-text-secondary text-left">
                          <th className="py-1 pr-2 font-medium">#</th>
                          <th className="py-1 pr-2 font-medium">Descrição</th>
                          <th className="py-1 pr-2 font-medium text-right">Qtd</th>
                          <th className="py-1 pr-2 font-medium text-right">Valor unit.</th>
                          <th className="py-1 font-medium text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.itens.map((item, idx) => (
                          <tr key={item.numero ?? idx} className="border-t border-ds-border align-top">
                            <td className="py-1.5 pr-2 text-ds-text-secondary">{item.numero ?? idx + 1}</td>
                            <td className="py-1.5 pr-2 text-ds-text-primary">{item.descricao || '—'}</td>
                            <td className="py-1.5 pr-2 text-right text-ds-text-primary whitespace-nowrap">
                              {item.quantidade != null ? item.quantidade : '—'}{item.unidade ? ` ${item.unidade}` : ''}
                            </td>
                            <td className="py-1.5 pr-2 text-right text-ds-text-primary whitespace-nowrap">
                              {item.valor_unitario != null ? formatMoney(item.valor_unitario) : '—'}
                            </td>
                            <td className="py-1.5 text-right font-medium text-ds-success whitespace-nowrap">
                              {item.valor_total != null ? formatMoney(item.valor_total) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </CollapsibleSection>

            <CollapsibleSection
              title="Arquivos do edital"
              icon={<FileText className="h-4 w-4 text-ds-text-secondary" />}
              badge={arquivosQuery.data && !arquivosQuery.data.indisponivel ? `(${arquivosQuery.data.arquivos.length})` : undefined}
              onOpenChange={setOpenArquivos}
            >
              {(() => {
                const d = arquivosQuery.data;
                const estado = (
                  <PncpEstado
                    isLoading={arquivosQuery.isLoading}
                    isError={arquivosQuery.isError}
                    indisponivel={d?.indisponivel}
                    origemRecusou={d?.origem_recusou}
                    fonteExterna={d?.fonte_externa}
                    vazio={Boolean(d && d.arquivos.length === 0)}
                    onRetry={() => arquivosQuery.refetch()}
                  />
                );
                if (!d || d.indisponivel || d.origem_recusou || d.arquivos.length === 0) return estado;
                return (
                  <ul className="space-y-2">
                    {d.arquivos.map((arq, idx) => (
                      <li key={arq.sequencial ?? idx} className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-ds-text-primary truncate">{arq.titulo || 'Documento'}</p>
                          <p className="text-xs text-ds-text-secondary">
                            {arq.tipo || 'Arquivo'}{arq.data_publicacao ? ` · ${formatDate(arq.data_publicacao)}` : ''}
                          </p>
                        </div>
                        {arq.url && (
                          <a href={arq.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                            <Button type="button" variant="secondary" size="sm">
                              <Download className="h-4 w-4 mr-2" />
                              Baixar
                            </Button>
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </CollapsibleSection>

            <CollapsibleSection
              title="Histórico da licitação"
              icon={<History className="h-4 w-4 text-ds-text-secondary" />}
              badge={historicoQuery.data && !historicoQuery.data.indisponivel ? `(${historicoQuery.data.historico.length})` : undefined}
              onOpenChange={setOpenHistorico}
            >
              {(() => {
                const d = historicoQuery.data;
                const estado = (
                  <PncpEstado
                    isLoading={historicoQuery.isLoading}
                    isError={historicoQuery.isError}
                    indisponivel={d?.indisponivel}
                    origemRecusou={d?.origem_recusou}
                    fonteExterna={d?.fonte_externa}
                    vazio={Boolean(d && d.historico.length === 0)}
                    onRetry={() => historicoQuery.refetch()}
                  />
                );
                if (!d || d.indisponivel || d.origem_recusou || d.historico.length === 0) return estado;
                return (
                  <ol className="space-y-3">
                    {d.historico.map((ev, idx) => (
                      <li key={idx} className="border-l-2 border-ds-border pl-3">
                        <p className="text-xs text-ds-text-secondary">{formatDateTime(ev.data)}</p>
                        <p className="text-sm text-ds-text-primary">
                          {ev.tipo || 'Evento'}{ev.categoria ? ` — ${ev.categoria}` : ''}
                        </p>
                        {(ev.documento_titulo || ev.documento_tipo) && (
                          <p className="text-xs text-ds-text-secondary">
                            {ev.documento_tipo}{ev.documento_titulo ? `: ${ev.documento_titulo}` : ''}
                          </p>
                        )}
                        {ev.responsavel && <p className="text-xs text-ds-text-secondary">Responsável: {ev.responsavel}</p>}
                      </li>
                    ))}
                  </ol>
                );
              })()}
            </CollapsibleSection>

            <CollapsibleSection
              title="Inteligência de mercado"
              icon={<TrendingUp className="h-4 w-4 text-ds-text-secondary" />}
              badge={codigoDetectado ? `CATMAT ${codigoDetectado}` : undefined}
              onOpenChange={setOpenMercado}
            >
              <div className="space-y-3">
                <div className="flex items-end gap-2">
                  <label className="flex-1">
                    <span className="mb-1 block text-[10px] uppercase tracking-wide text-ds-text-secondary">Código CATMAT (material)</span>
                    <input
                      className="w-full rounded-lg border border-ds-border bg-ds-surface px-3 py-2 text-sm text-ds-text-primary focus:outline-none focus:ring-2 focus:ring-ds-brand/50"
                      inputMode="numeric"
                      value={codigoManual}
                      onChange={(e) => setCodigoManual(e.target.value.replace(/\D/g, ''))}
                      placeholder={codigoDetectado ? `Detectado: ${codigoDetectado}` : 'Ex.: 268575'}
                    />
                  </label>
                  {codigoManual && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => setCodigoManual('')}>Limpar</Button>
                  )}
                </div>

                {!codigoAtivo && (
                  <p className="text-sm text-ds-text-secondary">
                    Nenhum código CATMAT detectado nos itens. Informe um código acima para ver os preços praticados.
                  </p>
                )}
                {codigoAtivo && benchmarkQuery.isLoading && <p className="text-sm text-ds-text-secondary">Consultando preços…</p>}
                {codigoAtivo && benchmarkQuery.isError && (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-ds-text-secondary">Não foi possível consultar os preços agora.</p>
                    <Button type="button" variant="secondary" size="sm" onClick={() => benchmarkQuery.refetch()}>Tentar novamente</Button>
                  </div>
                )}
                {(() => {
                  const b = benchmarkQuery.data;
                  if (!codigoAtivo || benchmarkQuery.isLoading || benchmarkQuery.isError || !b) return null;
                  if (b.indisponivel) {
                    return <p className="text-sm text-ds-text-secondary">{b.motivo === 'servico_sem_fonte_federal'
                      ? 'Benchmark disponível apenas para materiais (CATMAT) no momento.'
                      : 'Sem dados de preço para este código.'}</p>;
                  }
                  if (b.origem_recusou) {
                    return (
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm text-ds-text-secondary">A consulta foi recusada pela origem (Compras.gov).</p>
                        <Button type="button" variant="secondary" size="sm" onClick={() => benchmarkQuery.refetch()}>Tentar novamente</Button>
                      </div>
                    );
                  }
                  if (!b.amostras) return <p className="text-sm text-ds-text-secondary">Nenhum preço encontrado para o código {b.codigo}.</p>;
                  return (
                    <div className="space-y-2">
                      {b.descricao && <p className="text-xs text-ds-text-secondary">{b.descricao}</p>}
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg border border-ds-border bg-ds-surface p-2">
                          <p className="text-[10px] uppercase tracking-wide text-ds-text-secondary">Mínimo</p>
                          <p className="text-sm font-semibold text-ds-success">{b.minimo != null ? formatMoney(b.minimo) : '—'}</p>
                        </div>
                        <div className="rounded-lg border border-ds-brand/40 bg-ds-brand/5 p-2">
                          <p className="text-[10px] uppercase tracking-wide text-ds-text-secondary">Mediana</p>
                          <p className="text-sm font-semibold text-ds-text-primary">{b.mediana != null ? formatMoney(b.mediana) : '—'}</p>
                        </div>
                        <div className="rounded-lg border border-ds-border bg-ds-surface p-2">
                          <p className="text-[10px] uppercase tracking-wide text-ds-text-secondary">Máximo</p>
                          <p className="text-sm font-semibold text-ds-text-primary">{b.maximo != null ? formatMoney(b.maximo) : '—'}</p>
                        </div>
                      </div>
                      <p className="text-xs text-ds-text-secondary">
                        {b.amostras} compra{b.amostras === 1 ? '' : 's'} analisada{b.amostras === 1 ? '' : 's'}
                        {b.periodo_inicio ? ` · ${formatDate(b.periodo_inicio)}` : ''}{b.periodo_fim ? ` a ${formatDate(b.periodo_fim)}` : ''}
                        {' '}· fonte Compras.gov
                      </p>
                    </div>
                  );
                })()}
              </div>
            </CollapsibleSection>
          </div>
        </div>

        <div className="border-t border-ds-border pt-4 space-y-3">
          {isEmpresaView && empresaIdNum && (
            <div className="rounded-lg border border-ds-success/20 bg-ds-success/10 p-3 space-y-3">
              <div>
                <h4 className="text-sm font-semibold text-ds-text-primary">Ações da empresa</h4>
                <p className="mt-1 text-xs text-ds-text-secondary">
                  Prepare uma peça para {empresaSelecionada?.nome_fantasia || empresaSelecionada?.razao_social || 'a empresa selecionada'} usando o Redator Ninja.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {EMPRESA_PRE_DISPUTA_ACTIONS.map(({ tipo, label, icon: Icon }) => (
                  <Link
                    key={tipo}
                    to={`/agentes-ia/redator-ninja?empresaId=${empresaIdNum}&licitacaoId=${licitacao.id}&tipo=${tipo}`}
                    onClick={onClose}
                  >
                    <Button type="button" size="sm" variant="secondary">
                      <Icon className="mr-2 h-4 w-4" />
                      {label}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <h4 className="text-sm font-semibold text-ds-text-primary">
            {isEmpresaView ? 'Avaliar para esta empresa' : 'Avaliar para um cliente'}
          </h4>
          <p className="text-xs text-ds-text-secondary -mt-2">
            Roda o Oráculo do Edital: compara esta licitação contra o perfil técnico {isEmpresaView ? 'da empresa selecionada.' : 'do cliente escolhido.'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!isEmpresaView && <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-1" htmlFor="feed-detalhes-empresa">
                Cliente
              </label>
              <select
                id="feed-detalhes-empresa"
                className={selectClassName}
                value={empresaId}
                onChange={(e) => setEmpresaId(e.target.value)}
              >
                <option value="">Selecione um cliente</option>
                {empresas.map((empresa) => (
                  <option key={empresa.id} value={empresa.id}>{empresa.razao_social}</option>
                ))}
              </select>
            </div>}

            <div>
              <label className="block text-xs font-medium text-ds-text-secondary mb-1" htmlFor="feed-detalhes-documento">
                Documento técnico
              </label>
              <select
                id="feed-detalhes-documento"
                className={selectClassName}
                value={documentoId}
                onChange={(e) => setDocumentoId(e.target.value)}
                disabled={!empresaId || documentosLoading}
              >
                <option value="">
                  {empresaId ? 'Selecione um documento' : 'Selecione um cliente primeiro'}
                </option>
                {(documentos || []).map((documento) => (
                  <option key={documento.id} value={documento.id}>{documento.nome_arquivo}</option>
                ))}
              </select>
            </div>
          </div>

          {empresaId && !documentosLoading && (documentos || []).length === 0 && (
            <p className="text-xs text-ds-warning">
              Este cliente ainda não tem documentos técnicos cadastrados.{' '}
              <Link to={`/empresas/${empresaId}/perfil-tecnico`} className="underline" onClick={onClose}>
                Enviar documento
              </Link>
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleConsultar}
              disabled={!empresaIdNum || !documentoIdNum || isEnqueuing || isRunning}
              isLoading={isEnqueuing || isRunning}
            >
              {isEnqueuing || isRunning ? 'Consultando aderência...' : 'Rodar análise de aderência'}
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => empresaIdNum && adicionar.mutate(licitacao.id)}
              disabled={!empresaIdNum}
              isLoading={adicionar.isPending}
            >
              Adicionar ao pipeline{empresaIdNum ? ' deste cliente' : ''}
            </Button>
          </div>

          {adicionar.isError && (
            <p className="text-sm text-ds-danger">{extractErrorMessage(adicionar.error)}</p>
          )}

          {adicionar.isSuccess && (
            <div className="rounded-lg border border-ds-success/20 bg-ds-success/10 p-3 space-y-1">
              <p className="text-sm text-ds-success">Licitação adicionada ao pipeline.</p>
              <Link
                to={`/gestao-licitacoes?empresa=${empresaIdNum}`}
                className="text-xs font-medium text-ds-success hover:underline"
                onClick={onClose}
              >
                Ver na Gestão de Licitações
              </Link>
            </div>
          )}

          {jobStatus?.status === 'failed' && (
            <div className="flex items-start gap-2 rounded-lg border border-ds-danger/20 bg-ds-danger/10 p-3">
              <AlertTriangle className="h-4 w-4 text-ds-danger shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ds-danger">Falha ao consultar edital</p>
                <p className="text-xs text-ds-text-secondary mt-0.5">{jobStatus.failedReason}</p>
              </div>
            </div>
          )}

          {resultadoLoading && <p className="text-sm text-ds-text-secondary">Carregando análise...</p>}

          {resultado && (
            <div className="rounded-lg border border-ds-border bg-ds-surface2 p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide mb-1">Recomendação</p>
                  <Badge variant={RECOMENDACAO_MAP[resultado.recomendacao].variant}>
                    {RECOMENDACAO_MAP[resultado.recomendacao].label}
                  </Badge>
                </div>
                <div>
                  <p className="text-[10px] text-ds-text-secondary uppercase tracking-wide mb-1">Aderência ao perfil</p>
                  <AderenciaBar score={resultado.aderencia_score} />
                </div>
              </div>

              {resultado.resumo_cliente && (
                <div>
                  <p className="text-xs font-semibold text-ds-text-primary mb-1">Resumo para o cliente</p>
                  <p className="text-sm text-ds-text-primary">{resultado.resumo_cliente}</p>
                </div>
              )}

              {resultado.riscos.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-ds-text-primary">Riscos identificados</p>
                  {resultado.riscos.map((risco, index) => (
                    <div key={`${risco.tipo}-${index}`} className="rounded-lg border border-ds-border bg-ds-surface p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-ds-text-primary">{risco.tipo}</span>
                        <Badge variant={riscoBadgeVariant(risco.severidade)} size="sm">{risco.severidade}</Badge>
                      </div>
                      <p className="text-xs text-ds-text-secondary mt-1">{risco.descricao}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export { LicitacaoFeedDetalhesModal };
